import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '@lms/ui';
import {
  calculateAge,
  getParticipantBackground,
  normalizePhoneNumber,
  PARTICIPANT_EDUCATION_LEVELS,
  PARTICIPANT_OCCUPATIONS,
} from '@lms/shared';
import type {
  ParticipantEducationLevel,
  ParticipantOccupation,
  UserGender,
} from '@lms/shared/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { copy } from '../copy/copy';
import { useAuthStore } from '../store/auth';
import { FacelessAvatar } from './FacelessAvatar';
import { useLanguage } from '../lib/LanguageContext';
import { ProfileDialog } from './ProfileDialog';
import { invalidateProfileQueries } from '../lib/profile-cache';

interface EditProfileModalProps {
  onClose: () => void;
  onSaved?: () => void;
}
interface ProfileFormData {
  name: string;
  phone: string;
  gender: UserGender | '';
  dateOfBirth: string;
  domisili: string;
  occupation: ParticipantOccupation | '';
  occupationOther: string;
  educationLevel: ParticipantEducationLevel | '';
  educationOther: string;
}
type FieldErrors = Partial<Record<keyof ProfileFormData | 'avatar', string>>;

function initFormData(user: ReturnType<typeof useAuthStore.getState>['user']): ProfileFormData {
  const background = getParticipantBackground(user?.legacyMetadata);
  const date = user?.dateOfBirth ? new Date(user.dateOfBirth) : null;
  const metadataDomisili = user?.legacyMetadata?.domisili;
  return {
    name: user?.name || '',
    phone: user?.phone || '',
    gender: user?.gender === 'FEMALE' || user?.gender === 'MALE' ? user.gender : '',
    dateOfBirth: date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : '',
    domisili: user?.domisili ?? (typeof metadataDomisili === 'string' ? metadataDomisili : ''),
    occupation: background.occupation ?? '',
    occupationOther: background.occupationOther ?? '',
    educationLevel: background.educationLevel ?? '',
    educationOther: background.educationOther ?? '',
  };
}

const inputClass =
  'w-full rounded-lg bg-surface-container-high px-4 py-3 text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary disabled:opacity-40';

export function EditProfileModal({ onClose, onSaved }: EditProfileModalProps) {
  const { user } = useAuthStore();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [initial] = useState(() => initFormData(user));
  const [profileUserId] = useState(user?.id);
  const [formData, setFormData] = useState<ProfileFormData>(initial);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const avatarImage = useRef<HTMLImageElement>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirty =
    JSON.stringify(formData) !== JSON.stringify(initial) || avatarFile !== null || removeAvatar;

  useEffect(() => {
    if (!avatarFile) return;
    const url = URL.createObjectURL(avatarFile);
    if (avatarImage.current) avatarImage.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const updateMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const initiatingSession = useAuthStore.getState();
      if (!initiatingSession.token || initiatingSession.user?.id !== profileUserId)
        throw new Error(t(copy.profile.errorMsg));
      const updateData: Parameters<typeof apiClient.auth.updateProfile>[0] = {
        name: data.name.trim(),
      };
      if (data.gender) updateData.gender = data.gender;
      const phone = normalizePhoneNumber(data.phone);
      if (phone) updateData.phone = phone.value;
      updateData.dateOfBirth = data.dateOfBirth || null;
      updateData.domisili = data.domisili.trim();
      if (avatarFile) updateData.avatar = (await apiClient.auth.uploadAvatar(avatarFile)).url;
      else if (removeAvatar) updateData.avatar = null;
      if (data.occupation) {
        updateData.occupation = data.occupation;
        if (data.occupation === 'OTHER') updateData.occupationOther = data.occupationOther.trim();
      }
      if (data.educationLevel) {
        updateData.educationLevel = data.educationLevel;
        if (data.educationLevel === 'OTHER') updateData.educationOther = data.educationOther.trim();
      }
      const beforeUpdate = useAuthStore.getState();
      if (beforeUpdate.token !== initiatingSession.token || beforeUpdate.user?.id !== profileUserId)
        throw new Error(t(copy.profile.errorMsg));
      const updatedUser = await apiClient.auth.updateProfile(updateData);
      return { updatedUser, initiatingToken: initiatingSession.token };
    },
    onSuccess: ({ updatedUser, initiatingToken }) => {
      const current = useAuthStore.getState();
      if (current.token !== initiatingToken || current.user?.id !== updatedUser.id) return;
      current.setAuth(initiatingToken, updatedUser);
      invalidateProfileQueries(queryClient, updatedUser.id);
      onSaved?.();
      onClose();
    },
    onError: () => setErrorMessage(t(copy.profile.errorMsg)),
  });
  const close = () => {
    if (updateMutation.isPending) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };
  const change = <K extends keyof ProfileFormData>(key: K, value: ProfileFormData[K]) => {
    setFormData(previous => ({ ...previous, [key]: value }));
    setErrors(previous => ({ ...previous, [key]: undefined }));
    setErrorMessage('');
    setConfirmDiscard(false);
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (updateMutation.isPending || !dirty) return;
    const next: FieldErrors = {};
    const name = formData.name.trim();
    if (name.length < 2 || name.length > 100) next.name = t(copy.profile.nameInvalid);
    if (!formData.phone.trim() && initial.phone) next.phone = t(copy.profile.phoneRequired);
    else if (formData.phone.trim() && !normalizePhoneNumber(formData.phone))
      next.phone = t(copy.profile.phoneInvalid);
    if (formData.domisili.trim().length > 200) next.domisili = t(copy.profile.domisiliInvalid);
    if (
      formData.dateOfBirth &&
      (Number.isNaN(new Date(formData.dateOfBirth).getTime()) ||
        formData.dateOfBirth > new Date().toISOString().slice(0, 10))
    )
      next.dateOfBirth = t(copy.profile.dateInvalid);
    if (
      formData.occupation === 'OTHER' &&
      (formData.occupationOther.trim().length < 2 || formData.occupationOther.trim().length > 100)
    )
      next.occupationOther = t(copy.profile.backgroundInvalid);
    if (
      formData.educationLevel === 'OTHER' &&
      (formData.educationOther.trim().length < 2 || formData.educationOther.trim().length > 100)
    )
      next.educationOther = t(copy.profile.backgroundInvalid);
    if (!formData.occupation && initial.occupation)
      next.occupation = t(copy.profile.backgroundKeep);
    if (!formData.educationLevel && initial.educationLevel)
      next.educationLevel = t(copy.profile.backgroundKeep);
    if (errors.avatar) next.avatar = errors.avatar;
    setErrors(next);
    if (Object.keys(next).length) {
      document.getElementById(`profile-${Object.keys(next)[0]}`)?.focus();
      return;
    }
    setErrorMessage('');
    updateMutation.mutate(formData);
  };
  const field = (key: keyof FieldErrors, label: string, control: ReactNode, help?: string) => (
    <div>
      <label
        htmlFor={`profile-${key}`}
        className="mb-2 block text-sm font-label font-bold text-on-surface"
      >
        {label}
      </label>
      {control}
      {help && (
        <p id={`profile-${key}-help`} className="mt-2 text-xs leading-5 text-on-surface-variant">
          {help}
        </p>
      )}
      {errors[key] && (
        <p id={`profile-${key}-error`} role="alert" className="mt-2 text-sm text-error">
          {errors[key]}
        </p>
      )}
    </div>
  );
  const textInput = (key: keyof ProfileFormData, type = 'text', maxLength?: number) => (
    <input
      id={`profile-${key}`}
      type={type}
      value={formData[key]}
      maxLength={maxLength}
      className={inputClass}
      autoComplete={key === 'name' ? 'name' : key === 'phone' ? 'tel' : undefined}
      required={key === 'name' || (key === 'phone' && Boolean(initial.phone))}
      max={type === 'date' ? new Date().toISOString().slice(0, 10) : undefined}
      aria-invalid={Boolean(errors[key])}
      aria-describedby={
        [
          ['name', 'phone', 'domisili'].includes(key) ||
          (key === 'dateOfBirth' && calculateAge(formData.dateOfBirth) !== null)
            ? `profile-${key}-help`
            : '',
          errors[key] ? `profile-${key}-error` : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
      onChange={event => change(key, event.target.value as ProfileFormData[typeof key])}
    />
  );

  return (
    <ProfileDialog
      title={t(copy.profile.editProfile)}
      description={t(copy.profile.editDescription)}
      busy={updateMutation.isPending}
      onClose={close}
    >
      <form
        onSubmit={submit}
        noValidate
        className="flex min-h-0 flex-col"
        aria-busy={updateMutation.isPending}
      >
        <div className="overflow-y-auto px-6 pb-6">
          {errorMessage && (
            <p
              role="alert"
              className="mb-6 rounded-lg bg-error-container p-4 text-sm text-on-error-container"
            >
              {errorMessage}
            </p>
          )}
          <fieldset disabled={updateMutation.isPending} className="space-y-6">
            {field(
              'avatar',
              t(copy.profile.avatar),
              <>
                <div className="mb-3 flex items-center gap-4">
                  <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary text-on-primary">
                    {avatarFile || (!removeAvatar && user?.avatar) ? (
                      <img
                        ref={avatarImage}
                        src={avatarFile ? undefined : user?.avatar}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : (
                      <FacelessAvatar className="size-12" />
                    )}
                  </div>
                  {(avatarFile || (!removeAvatar && user?.avatar)) && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setAvatarFile(null);
                        setRemoveAvatar(Boolean(user?.avatar));
                        setErrors(previous => ({ ...previous, avatar: undefined }));
                      }}
                    >
                      {t(copy.profile.removeAvatar)}
                    </Button>
                  )}
                </div>
                <input
                  id="profile-avatar"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className={`${inputClass} text-sm`}
                  aria-describedby={`profile-avatar-help${errors.avatar ? ' profile-avatar-error' : ''}`}
                  aria-invalid={Boolean(errors.avatar)}
                  onChange={event => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    if (
                      !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) ||
                      file.size > 5 * 1024 * 1024 ||
                      file.size === 0
                    ) {
                      setErrors(previous => ({
                        ...previous,
                        avatar: t(copy.profile.avatarInvalid),
                      }));
                      event.target.value = '';
                      return;
                    }
                    setAvatarFile(file);
                    setRemoveAvatar(false);
                    setErrors(previous => ({ ...previous, avatar: undefined }));
                    event.target.value = '';
                  }}
                />
              </>,
              t(copy.profile.avatarHelp)
            )}
            <h3 className="font-headline text-lg font-bold text-on-surface">
              {t(copy.profile.identitySection)}
            </h3>
            {field(
              'name',
              t(copy.profile.name),
              textInput('name', 'text', 100),
              t(copy.profile.nameHelp)
            )}
            {field(
              'phone',
              t(copy.profile.phone),
              textInput('phone', 'tel'),
              t(initial.phone ? copy.profile.phoneRequired : copy.profile.phoneHelp)
            )}
            {field(
              'domisili',
              t(copy.profile.domisili),
              textInput('domisili', 'text', 200),
              t(copy.profile.domisiliHelp)
            )}
            <div className="grid gap-6 sm:grid-cols-2">
              {field(
                'gender',
                t(copy.profile.gender),
                <select
                  id="profile-gender"
                  value={formData.gender}
                  className={inputClass}
                  onChange={event => change('gender', event.target.value as UserGender | '')}
                >
                  <option value="" disabled={Boolean(initial.gender)}>
                    {t(copy.common.notSet)}
                  </option>
                  <option value="MALE">{t(copy.profile.genderMale)}</option>
                  <option value="FEMALE">{t(copy.profile.genderFemale)}</option>
                </select>
              )}
              {field(
                'dateOfBirth',
                t(copy.profile.dateOfBirth),
                textInput('dateOfBirth', 'date'),
                calculateAge(formData.dateOfBirth) !== null
                  ? t(copy.profile.ageSummary).replace(
                      '{age}',
                      String(calculateAge(formData.dateOfBirth))
                    )
                  : undefined
              )}
            </div>
            <h3 className="font-headline text-lg font-bold text-on-surface">
              {t(copy.profile.backgroundSection)}
            </h3>
            {field(
              'occupation',
              t(copy.participantBackground.occupationLabel),
              <select
                id="profile-occupation"
                value={formData.occupation}
                className={inputClass}
                aria-invalid={Boolean(errors.occupation)}
                aria-describedby={`profile-occupation-help${errors.occupation ? ' profile-occupation-error' : ''}`}
                onChange={event =>
                  change('occupation', event.target.value as ParticipantOccupation | '')
                }
              >
                <option value="" disabled={Boolean(initial.occupation)}>
                  {t(copy.participantBackground.chooseOccupation)}
                </option>
                {PARTICIPANT_OCCUPATIONS.map(option => (
                  <option key={option} value={option}>
                    {t(copy.participantBackground.options[option])}
                  </option>
                ))}
              </select>,
              t(
                initial.occupation
                  ? copy.profile.backgroundKeep
                  : copy.participantBackground.occupationHelp
              )
            )}
            {formData.occupation === 'OTHER' &&
              field(
                'occupationOther',
                t(copy.participantBackground.occupationOtherLabel),
                textInput('occupationOther', 'text', 100)
              )}
            {field(
              'educationLevel',
              t(copy.participantBackground.educationLabel),
              <select
                id="profile-educationLevel"
                value={formData.educationLevel}
                className={inputClass}
                aria-invalid={Boolean(errors.educationLevel)}
                aria-describedby={`profile-educationLevel-help${errors.educationLevel ? ' profile-educationLevel-error' : ''}`}
                onChange={event =>
                  change('educationLevel', event.target.value as ParticipantEducationLevel | '')
                }
              >
                <option value="" disabled={Boolean(initial.educationLevel)}>
                  {t(copy.participantBackground.chooseEducation)}
                </option>
                {PARTICIPANT_EDUCATION_LEVELS.map(option => (
                  <option key={option} value={option}>
                    {t(copy.participantBackground.options[option])}
                  </option>
                ))}
              </select>,
              t(
                initial.educationLevel
                  ? copy.profile.backgroundKeep
                  : copy.participantBackground.educationHelp
              )
            )}
            {formData.educationLevel === 'OTHER' &&
              field(
                'educationOther',
                t(copy.participantBackground.educationOtherLabel),
                textInput('educationOther', 'text', 100)
              )}
          </fieldset>
        </div>
        <footer className="shrink-0 bg-surface-container-low px-6 py-4">
          {confirmDiscard ? (
            <div role="alert" className="space-y-3">
              <p className="font-bold text-on-surface">{t(copy.profile.discardTitle)}</p>
              <p className="text-sm text-on-surface-variant">
                {t(copy.profile.discardDescription)}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button type="button" variant="ghost" onClick={() => setConfirmDiscard(false)}>
                  {t(copy.profile.keepEditing)}
                </Button>
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t(copy.profile.discard)}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                disabled={updateMutation.isPending}
              >
                {t(copy.common.cancel)}
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || !dirty}
                className="bg-gradient-to-r from-primary to-primary-container"
              >
                {t(updateMutation.isPending ? copy.profile.saving : copy.common.save)}
              </Button>
            </div>
          )}
        </footer>
      </form>
    </ProfileDialog>
  );
}
